import { ChartContainer } from 'components/ui/chart';
import { FormattedMessage } from 'react-intl';
import { Pie, PieChart } from 'recharts';
import { CHART_COLORS } from 'utils/chartColors';
import { cn } from 'utils/cn';
import { FigureFormat, formatCompact, formatPercent } from './figureFormat';
import {
    FigureLink,
    Headline,
    KpiFormProps,
    RowLabel,
    rowLink,
    TargetStrip,
    topAndRest,
    VALUE_CLASS,
} from './KpiParts';

/** The slices before everything else becomes one: with it, one slice per chart colour. */
const TOP_SLICES = CHART_COLORS.length - 1;

/** The ring of each size: its width across and the radius of its hole. */
const RING = { small: { size: 104, innerRadius: 36 }, wide: { size: 140, innerRadius: 48 } };

/**
 * A breakdown as a ring with its legend, each legend row a link to that slice's records. Two columns wide the ring
 * is larger and a row also carries the slice's share, taken of the slices drawn and never of the total, which a
 * record counted under two values makes smaller than their sum.
 */
export function BreakdownDonut({ name, definition, figure, size, links, format }: KpiFormProps) {
    const wide = size === '2x2';
    const ring = wide ? RING.wide : RING.small;
    // A ring has no negative slice: the groups that take away are left out, and so is a rest that nets below zero.
    const positive = {
        ...figure,
        rows: figure.rows?.filter((row) => row.value > 0),
        otherValue: Math.max(figure.otherValue ?? 0, 0),
    };
    const slices = topAndRest(positive, TOP_SLICES).map((row, index) => ({
        ...row,
        fill: CHART_COLORS[index % CHART_COLORS.length],
    }));
    const drawn = slices.reduce((sum, slice) => sum + slice.value, 0);
    const total = (
        <Headline
            key="total"
            value={figure.value}
            format={format}
            to={links?.list}
            className="text-foreground font-semibold"
        />
    );
    // A slice's value that leaves its name no room is written as a bar's label is: its unit is the total's, above.
    const sliceFormat: FigureFormat = (value, short) => (short ? formatCompact(value) : format(value));

    return (
        <>
            {definition.target ? (
                <TargetStrip figure={figure} target={definition.target} format={format} lead={total} />
            ) : (
                <span className="text-meta text-muted-foreground truncate">
                    <FormattedMessage id="home.widgets.total" values={{ value: total }} />
                </span>
            )}
            <div className={cn('flex min-h-0 flex-1 items-center', wide ? 'gap-8' : 'gap-[18px]')}>
                <ChartContainer
                    config={{}}
                    initialDimension={{ width: ring.size, height: ring.size }}
                    role="img"
                    aria-label={name}
                    className={cn('aspect-square shrink-0', wide ? 'size-35' : 'size-[104px]')}
                >
                    <PieChart accessibilityLayer={false}>
                        {/* Starts at 12 o'clock and runs clockwise. */}
                        <Pie
                            data={slices}
                            dataKey="value"
                            innerRadius={ring.innerRadius}
                            outerRadius={ring.size / 2}
                            startAngle={90}
                            endAngle={-270}
                            stroke="var(--color-paper)"
                            isAnimationActive={false}
                            rootTabIndex={-1}
                        />
                    </PieChart>
                </ChartContainer>
                <ul className={cn('flex min-w-0 flex-1 flex-col', !wide && 'gap-1.5')}>
                    {slices.map((slice) => (
                        <li
                            key={slice.key ?? (slice.isRest ? 'rest' : 'none')}
                            className={cn(wide && 'border-divider-soft border-t')}
                        >
                            <FigureLink
                                to={rowLink(links, slice)}
                                className={cn('group/slice text-meta flex items-center gap-2', wide && 'h-[27px]')}
                            >
                                <svg aria-hidden width="8" height="8" viewBox="0 0 8 8" className="shrink-0">
                                    <rect width="8" height="8" rx="2" fill={slice.fill} />
                                </svg>
                                {/* The name keeps 64px beside a value in full, and what a short one leaves it. */}
                                <span className="text-foreground-secondary min-w-16 flex-1 truncate group-has-[[data-short]]/slice:min-w-0">
                                    <RowLabel row={slice} />
                                </span>
                                <Headline value={slice.value} format={sliceFormat} className={VALUE_CLASS} />
                                {wide ? (
                                    <span className={cn(VALUE_CLASS, 'text-muted-foreground w-10 text-end')}>
                                        {formatPercent(drawn ? slice.value / drawn : 0)}
                                    </span>
                                ) : null}
                            </FigureLink>
                        </li>
                    ))}
                </ul>
            </div>
        </>
    );
}
